import React, { useState, useContext, useEffect, useRef } from 'react';
import { Grid, Box, Button, TextField, Container } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material/';
import '../../assets/css/buttons.css';

import ClubHouseLogo from '../../assets/img/clubhouse_logo.png';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/Context';
import LoginIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';

import { SocketContext } from '../../context/SocketContext';
import { SocketNFCContext } from '../../context/SocketNFC';

import toast, { Toaster } from 'react-hot-toast';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { maskedFieldProps } from '../../utils/maskedField';

// Touch-sized, brand-tinted field; deliberately not .modal-textfield, which would win the tie
const fieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: '76px',
    borderRadius: '12px',
    backgroundColor: 'rgba(35, 42, 69, 0.55)',
    transition: 'box-shadow 160ms ease, background-color 160ms ease',
  },
  '& .MuiInputBase-input': {
    fontSize: '24px',
    color: '#e6eaf2',
    padding: '0 16px',
    height: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiInputLabel-root': {
    fontSize: '22px',
    color: '#8fa3d4',
    // MUI's resting offset assumes a 56px field; this one is 76px, so the label sat high
    transform: 'translate(16px, 22px) scale(1)',
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    transform: 'translate(16px, -10px) scale(0.75)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#c08cdf',
  },
  // The legend is sized in em off the fieldset, so the notch has to be scaled by hand
  '& .MuiOutlinedInput-notchedOutline legend': {
    fontSize: '16.5px',
  },
  '& .MuiOutlinedInput-root fieldset': {
    borderColor: '#2a3a6e',
    borderWidth: '1px',
  },
  // The tube lighting up. Focus, not hover — hover sticks on a touch panel.
  '& .MuiOutlinedInput-root.Mui-focused': {
    backgroundColor: 'rgba(35, 42, 69, 0.75)',
    boxShadow: '0 0 0 3px rgba(192, 140, 223, 0.13)',
  },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
    borderColor: '#c08cdf',
    borderWidth: '2px',
  },
};

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    updateIsFirstShiftOfTheDay,
    updatePreviousShiftClose,
    updateShowModalStartShift,
    updateMaximumAnonPrizeAllowed,
    updateKiosk,
    updateIsPapirEnabled,
    updateIsPapirKingoEnabled,
    updateIsPapirXtippEnabled,
    updateIsFlaxEnabled,
    updateIsSystemLockEnabled,
    updateIsDataBingoBlocked,
    updateIsMainGameBlocked,
    isLogin,
    updateLogin,
    modifyOperator,
    isBlocking,
    BlockingScreen,
    updateSession,
    modifyOperatorFull,
    modifyOperatorInfo,
    modifyHall,
    updatePrinter,
    updatePrinterIP,
    updateCardID,
    updateOKBingoID,
    updateIsOKBingoEnabled,
    enviroment,
    isNTEnabled,
    updateIsNTEnabled,
    isKioskEnabled,
    updateIsKioskEnabled,
    updateBingoID,
    updateIsEnabledBankID,
    updateIsWGEnabled,
    updateWGBingoID,
    updateIsAdmin,
    updateCloseTime,
    updateIsLoadingScreen,
  } = useContext(AppContext);

  // Ref keeps the synchronous re-entrancy guard, state drives the button; a plain var guarded nothing
  const isLoadingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const { socket } = useContext(SocketContext);
  const socketNFC = useContext(SocketNFCContext);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Stage switcher — only shown on the stage01/stage02 environments.
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const currentStage = hostname.includes('stage01')
    ? 'stage01'
    : hostname.includes('stage02')
      ? 'stage02'
      : null;
  const targetStage =
    currentStage === 'stage01'
      ? 'stage02'
      : currentStage === 'stage02'
        ? 'stage01'
        : null;

  const [showStageModal, setShowStageModal] = useState(false);
  const [isSwitchingStage, setIsSwitchingStage] = useState(false);

  const stopLoading = () => {
    isLoadingRef.current = false;
    setIsLoading(false);
  };

  const switchStageTask = async () => {
    if (!targetStage || isSwitchingStage) {
      return;
    }

    setIsSwitchingStage(true);
    try {
      const url = `https://${currentStage}-operator.vpfscandinavia.com/api/switch-stage`;
      console.log('Switch stage URL: ' + url);
      await axios.post(url, { stage: targetStage });
      toast.success(t('login.switchStageSuccess', { target: targetStage }));
      setShowStageModal(false);
      updateIsLoadingScreen(true);
    } catch (err) {
      console.error(err);
      toast.error(t('login.switchStageError'));
    } finally {
      setIsSwitchingStage(false);
    }
  };

  const loginTask = async () => {
    if (isLoadingRef.current) {
      return;
    }

    if (!username || !password) {
      toast.error(t('error.empty'));
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);

    let dataRequest = {
      username: username,
      password: password,
    };

    try {
      const hostname = window.location.hostname;
      var url = 'https://stage01-operator.vpfscandinavia.com';

      if (hostname === 'localhost') {
        url = 'https://stage01-operator.clubhouse.no/api/login';
      } else if (hostname.includes('stage01')) {
        url = 'https://stage01-operator.clubhouse.no/api/login/';
      } else if (hostname.includes('stage02')) {
        url = 'https://stage02-operator.clubhouse.no/api/login/';
      } else {
        url = 'https://operator.clubhouse.no/api/login/';
      }

      console.log('Login URL: ' + url);
      console.log(dataRequest);

      var response = await axios({
        method: 'post',
        url: url,
        data: dataRequest,
      });

      console.log(response);

      //const cookieString = response.headers["set-cookie"][0];
      //const sessValue = cookieString.split(";")[0].split("=")[1];

      const sessValue = response.data.operator.auth;
      response.data.session = sessValue;

      //Temp get countries
      //socket.emit("getCountries", response.data);

      if (!response) {
        stopLoading();
        toast.error(t('error.login'));
        return;
      }

      updateSession(sessValue);
      modifyOperator(response.data.operator.username);
      modifyOperatorFull(response.data.operator.firstName);
      modifyHall(response.data.hall.name);
      modifyOperatorInfo(response.data);
      updateBingoID(response.data.hall.id);

      stopLoading();

      var dataSocket = {
        operator: username,
        password: password,
        sessionID: sessValue,
        operatorInfo: response.data,
        platform: 'cashier',
      };
      socket.emit('registerLogin', dataSocket);

      //navigate("/salgs");
    } catch (err) {
      console.error(err);
      stopLoading();
      toast.error(t('error.login'));
      return;
    }

    /*
    var data = {
      username: username,
      password: password,
      action: "login",
    };
    socket.emit("login", data);
    */
  };

  // The keydown effect registers once, so this ref keeps it pointing at the live closure
  const loginTaskRef = useRef(loginTask);
  loginTaskRef.current = loginTask;

  useEffect(() => {
    const handleLogin = (data) => {
      if (!data) {
        stopLoading();
        toast.error(t('error.login'));
        return;
      }

      if (!data) {
        stopLoading();
        toast.error(t('error.login'));
        return;
      }
      modifyOperator(data.operator.username);
      modifyOperatorFull(data.operator.firstName);
      modifyHall(data.hall.name);
      updateLogin(true);
      updateSession(data.session);
      stopLoading();

      socket.emit('registerLogin', data);
      //navigate("/salgs");
    };

    const handleGetCountries = (data) => {
      //console.log("countries");
      //console.log(data);
    };

    const handleRegisterLogin = (data) => {
      console.log('handleRegisterLogin', data);

      if (data.printerIp) {
        updatePrinterIP(data.printerIp);
        updatePrinter({
          text: '',
          name: '',
          id: '',
          ip: data.printerIp,
        });
      }

      if (data.schedule) {
        updateCloseTime(data.schedule.closeTime);
      }

      if (data.isEnabledBankID) {
        updateIsEnabledBankID(true);
      }

      if (data.isAdmin) {
        updateIsAdmin(true);
      } else {
        updateIsAdmin(false);
      }

      updateCardID(data.terminalID ?? '');

      for (var i = 0; i < data.bingoList.length; i++) {
        if (
          data.bingoList[i].bingoName === data.operatorInfo.hall.name &&
          data.bingoList[i].isEnabled
        ) {
          updateOKBingoID(data.bingoList[i].bingoID);
          updateIsOKBingoEnabled(true);
          break;
        }
      }

      for (var i = 0; i < data.isWGEnabled.length; i++) {
        if (
          data.isWGEnabled[i].bingoName === data.operatorInfo.hall.name &&
          data.isWGEnabled[i].isEnabled
        ) {
          updateWGBingoID(data.isWGEnabled[i].bingoID);
          updateIsWGEnabled(true);
          break;
        }
      }

      for (var i = 0; i < data.isNTEnabled.length; i++) {
        if (
          data.isNTEnabled[i].bingoName === data.operatorInfo.hall.name &&
          data.isNTEnabled[i].isEnabled
        ) {
          updateIsNTEnabled(true);
          break;
        }
      }

      for (var i = 0; i < data.isPapirEnabled.length; i++) {
        if (
          data.isPapirEnabled[i].bingoName === data.operatorInfo.hall.name &&
          data.isPapirEnabled[i].isEnabled
        ) {
          updateIsPapirEnabled(true);
          break;
        }
      }

      for (var i = 0; i < data.isPapirKingoEnabled.length; i++) {
        if (
          data.isPapirKingoEnabled[i].bingoName ===
            data.operatorInfo.hall.name &&
          data.isPapirKingoEnabled[i].isEnabled
        ) {
          updateIsPapirKingoEnabled(true);
          break;
        }
      }

      for (var i = 0; i < data.isPapirXtippEnabled.length; i++) {
        if (
          data.isPapirXtippEnabled[i].bingoName ===
            data.operatorInfo.hall.name &&
          data.isPapirXtippEnabled[i].isEnabled
        ) {
          updateIsPapirXtippEnabled(true);
          break;
        }
      }

      for (var i = 0; i < data.isKioskEnabled.length; i++) {
        if (
          data.isKioskEnabled[i].bingoName === data.operatorInfo.hall.name &&
          data.isKioskEnabled[i].isEnabled
        ) {
          updateIsKioskEnabled(true);
          break;
        }
      }

      for (var i = 0; i < data.isFlaxEnabled.length; i++) {
        if (
          data.isFlaxEnabled[i].bingoName === data.operatorInfo.hall.name &&
          data.isFlaxEnabled[i].isEnabled
        ) {
          updateIsFlaxEnabled(true);
          break;
        }
      }

      updateKiosk(data.kiosk);
      updateMaximumAnonPrizeAllowed(data.maximumAnonPrizeAllowed);

      /*
      if (data.isMainGameBlockEnabled === 1) {
        updateIsMainGameBlocked(true);
      }

      if (data.isDataBingoBlockEnabled === 1) {
        updateIsDataBingoBlocked(true);
      }
      */

      if (data.isSystemLockEnabled === 1) {
        updateIsSystemLockEnabled(true);
      }

      if (!data.isOpenShift) {
        updateShowModalStartShift(true);
      }

      if (data.isFirstOfTheDay) {
        updateIsFirstShiftOfTheDay(true);
      }

      updatePreviousShiftClose(data.previousShiftClose ?? null);

      updateLogin(true);
      navigate('/salgs');
    };

    socket.on('registerLogin', handleRegisterLogin);
    socket.on('login', handleLogin);
    socket.on('getCountries', handleGetCountries);

    return () => {
      socket.off('registerLogin', handleRegisterLogin);
      socket.off('login', handleLogin);
      socket.off('getCountries', handleGetCountries);
    };
  }, [socket]);

  useEffect(() => {
    return () => {};
  }, [socketNFC]);

  useEffect(() => {
    if (isLogin) {
      navigate('/salgs');
      return;
    }

    // Enter used to call handleFormSubmit, which only preventDefaults
    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        loginTaskRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const handleFormSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
  };

  return (
    <>
      {isBlocking && <BlockingScreen />}

      {currentStage && (
        <Box
          sx={{
            position: 'fixed',
            top: 12,
            right: 16,
            zIndex: 1300,
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={() => setShowStageModal(true)}
            sx={{
              textTransform: 'none',
              color: '#b6a3bf',
              fontSize: '15px',
              fontWeight: 600,
            }}
          >
            {t('login.switchStageLabel', {
              current: currentStage,
              target: targetStage,
            })}
          </Button>
        </Box>
      )}

      {showStageModal && (
        <div id="react-confirm-alert">
          <div className="react-confirm-alert-overlay">
            <div className="react-confirm-alert">
              <div
                className="react-confirm-alert-body"
                style={{ width: '760px' }}
              >
                <h1>{t('login.switchStageTitle', { target: targetStage })}</h1>
                <p>{t('login.switchStageMessage')}</p>

                <div className="react-confirm-alert-button-group">
                  <button
                    className="btn-custom-yes"
                    onClick={() => {
                      switchStageTask();
                    }}
                    disabled={isSwitchingStage}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                    {isSwitchingStage
                      ? t('login.switchStageSwitching')
                      : t('sales.confirm')}
                  </button>

                  <button
                    className="btn-custom-no"
                    onClick={() => {
                      setShowStageModal(false);
                    }}
                    disabled={isSwitchingStage}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CancelIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Neon glow fixed to the viewport so the Container's maxWidth doesn't crop it */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          background:
            'radial-gradient(ellipse 55% 45% at 50% 34%, rgba(197,126,218,0.085) 0%, rgba(197,126,218,0.040) 38%, rgba(228,96,156,0.018) 62%, rgba(8,20,30,0) 82%)',
        }}
      />

      <Container
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <Toaster position="top-center" reverseOrder={false} />

        <Box
          component="img"
          sx={{
            height: '300px',
            width: 'auto',
            paddingTop: 0,
            position: 'relative',
            zIndex: 1,
            // Lets the sign's own glow bleed past the PNG's edges
            filter:
              'drop-shadow(0 0 22px rgba(228,96,156,0.16)) drop-shadow(0 0 52px rgba(197,126,218,0.09))',
          }}
          alt=""
          src={ClubHouseLogo}
        />

        <form
          noValidate
          autoComplete="off"
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '460px',
            minWidth: '460px',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
          onSubmit={handleFormSubmit}
        >
          <Grid container direction={'column'} spacing={4}>
            <Grid item style={{ width: '100%' }}>
              <TextField
                autoFocus
                sx={fieldSx}
                label={t('login.username')}
                variant="outlined"
                onChange={(event) => {
                  setUsername(event.target.value);
                }}
              />
            </Grid>
            <Grid item style={{ width: '100%' }}>
              <TextField
                sx={fieldSx}
                label={t('login.password')}
                {...maskedFieldProps()}
                variant="outlined"
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
            </Grid>
            <Grid item style={{ width: '100%' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  height: '76px',
                  width: '100%',
                  borderRadius: '12px',
                  fontFamily: 'inherit',
                  fontSize: '24px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  color: '#ffffff',
                  textTransform: 'uppercase',
                  // From the logo's violet halo (H306→322), not its rose, which reads as the cancel tone
                  background:
                    'linear-gradient(180deg, #ad81da 0%, #954da0 100%)',
                  boxShadow: '0 6px 22px rgba(149, 77, 160, 0.22)',
                  '& .MuiButton-startIcon svg': { fontSize: '28px' },
                  '&:active': {
                    background:
                      'linear-gradient(180deg, #9a6fc6 0%, #853f90 100%)',
                    boxShadow: '0 2px 10px rgba(149, 77, 160, 0.26)',
                    transform: 'translateY(1px)',
                  },
                  '&.Mui-disabled': {
                    background:
                      'linear-gradient(180deg, #6b5a7d 0%, #4e3d56 100%)',
                    color: '#b6a3bf',
                    boxShadow: 'none',
                  },
                }}
                startIcon={<LoginIcon />}
                onClick={() => {
                  loginTask();
                }}
              >
                {t('login.login')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Container>
    </>
  );
}

export default Login;
